
// Packages
import { defineComponent, PropType } from 'vue';
import VueI18n from 'vue-i18n';

// Types
import type {
  CartItem,
  Total,
  Receipt,
  Bundle,
} from '@white-label-types/parking-checkout';
import type { TaxFee, BookingFee } from '@white-label-types/parking-booking';

// Helpers
import { formatPrice } from '@white-label-helper/helper-payment';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { PRODUCT_TYPES, NAMED_ROUTES, ROUTE_NAMES, FEATURE_FLAGS } from '@white-label-configuration/constants';

// Components
import SummaryPreloader from '../summary-preloader/summary-preloader.vue';
import SummarySubtitle from '../summary-subtitle/summary-subtitle.vue';
import ProductSummaryCard from '../product-summary-card/product-summary-card.vue';
import ProductSummaryBundleCard from '../product-summary-card/product-summary-bundle-card.vue';
import ProductSummaryTravelInsuranceCard from '../product-summary-card/product-summary-travel-insurance-card.vue';
import SummaryAirport from '../summary-airport/summary-airport.vue';
import IconPriceTag from '@white-label-icon/icon-price-tag';

// Stores
import useDepositStore from '@white-label-store/deposits'
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';

type SummaryElementItems = CartItem[] | Receipt['items'];
type SubItems = {
  title: string;
  value: string | Intl.NumberFormat;
  isDiscount?: boolean;
};

type InfoMessage = { text: string; slot0?: string };

export default defineComponent({
  name: 'PaymentSummary',

  components: {
    DepositBreakdown: () => import('./deposit-breakdown.vue'),
    IconAlert: () => import('@white-label-icon/icon-alert'),
    IconCheck: () => import('@white-label-icon/icon-check'),
    IconMessage: () => import('../icon-message/icon-message.vue'),
    IconArrow: () => import('@white-label-icon/icon-arrow'),
    SummaryPreloader,
    SummarySubtitle,
    ProductSummaryCard,
    ProductSummaryBundleCard,
    ProductSummaryTravelInsuranceCard,
    SummaryAirport,
    IconPriceTag
  },

  props: {
    amountToPay: {
      type: [Number, null] as PropType<number | null>,
      default: null,
    },
    contentLoading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<SummaryElementItems>,
      default: () => [] as SummaryElementItems,
    },
    bundle: {
      type: Object as PropType<Bundle | null>,
      default: null,
    },
    originalOrder: {
      type: Object as PropType<SummaryElementItems[0]>,
      required: false,
      default: () => ({}),
    },
    hasPaid: {
      type: Boolean,
      default: false,
    },
    isModifyFlowReceipt: {
      type: Boolean,
      default: false,
    },
    isRefund: {
      type: Boolean,
      default: false,
    },
    isPartialRefund: {
      type: Boolean,
      default: false,
    },
    orderTotals: {
      type: Object as PropType<Total>,
      default: () => ({}),
    },
    priceDifference: {
      type: String,
      default: '',
    },
    amendmentFee: {
      type: String,
      default: '',
    },
    cancellationFee: {
      type: String,
      default: '',
    },
    refundDoesNotExist: {
      type: Boolean,
      default: false,
    },
    refundProcessing: {
      type: Boolean,
      default: false,
    },
    valueAfterRefundWithoutCancellation: {
      type: String,
      default: '',
    },
    originalOrderHasCancellationProtection: {
      type: Boolean,
      default: false,
    },
    isSticky: {
      type: Boolean,
      default: false,
    },
    bookingFee: {
      type: [Object, Array] as PropType<BookingFee | []>,
      default: () => ({}),
    },
    cancellableItemsIds: {
      type: [Array] as PropType<number | string[]>,
      default: () => null,
    },
    forceTaxesBreakdown: {
      type: Boolean,
      default: false,
    },
    discountCode: {
      type: String,
      default: '',
    }
  },

  data() {
    return {
      dropdownIsActive: false,
      isExpanded: false,
      isCancellationProtectionExpanded: false,
      showDiscountBreakdown: false,
      showTaxesBreakdown: false,
      showBasket: false,
      isExtrasRoute:false,
    };
  },
  computed: {
    parkingExtensionEnabled() {
        return getFeatureFlag('PT_2973_PARKING_BOOKING_EXTENSION') === true
    },
    showProductBeingAmended() {
      return !!(this.isAmending && this.originalOrderProps.orderName && !this.isExtending)
    },
    isExtending(): boolean {
      const item = this.items[0];
      return item && 'meta' in item && !!item.meta?.upgrading_order_item_id;
    },
    isExtended(): boolean {
      const item = this.items[0];
      return item && !!item?.upgraded_from_order_item_id;
    },
    discountTotal(): string {
      if (!this.displayDiscountSection) return '';
      return this.isExtended ? this.discountItems?.[0]?.price : `-${formatPrice(this.orderTotals.discount)}`;
    },
    originalExitDate(): string | null {
      return this.items?.[0]?.search_criteria?.parking?.date1 || null;
    },
    originalExitTime(): string | null {
      return this.items?.[0]?.search_criteria?.parking?.time1 || null;
    },
    originalExit(): string | null {
      const date = this.originalExitDate;
      const time = this.originalExitTime;
      if (!date || !time) return null;
      return `${date}T${time}:00`;
    },

    iconArrowClassesOne() {
      return [
        'ml-2',
        { 'transform rotate-180': !this.showBasket }
      ]
    },

    iconArrowClassesTwo() {
      return [
        'text-mag-elements-icons-on-dark hide-on-print',
        { 'transform rotate-180': this.showDiscountBreakdown }
      ]
    },

    iconArrowClassesThree() {
      return [
        'text-mag-elements-icons-on-dark hide-on-print',
        { 'transform rotate-180': this.showTaxesBreakdown }
      ]
    },

    sortedItems(): SummaryElementItems {
      return this.items.slice().sort((a, b) => {
        const aIsExtrasOrInsurance = a.product_code === PRODUCT_TYPES.EXTRAS || a.product_code === PRODUCT_TYPES.TRAVEL_INSURANCE;
        const bIsExtrasOrInsurance = b.product_code === PRODUCT_TYPES.EXTRAS || b.product_code === PRODUCT_TYPES.TRAVEL_INSURANCE;

        if (aIsExtrasOrInsurance && !bIsExtrasOrInsurance) {
          return 1;
        }
        if (!aIsExtrasOrInsurance && bIsExtrasOrInsurance) {
          return -1;
        }

        const dateA = new Date(a.details?.entry_date).getTime();
        const dateB = new Date(b.details?.entry_date).getTime();

        if (dateA === dateB) {
          const nameA = (a.inventory_item.display_name || a.inventory_item.name)?.toLowerCase();
          const nameB = (b.inventory_item.display_name || b.inventory_item.name)?.toLowerCase();
          return nameA.localeCompare(nameB);
        }

        return dateA - dateB;
      }).filter((item) => !item.bundle_id);
    },

    discountItems(): { name: string, price: string } {
      const items = this.sortedItems
        .filter((item) => item.totals.discount !== 0)
        .map((item) => ({
          name: item.inventory_item.display_name || item.inventory_item.name,
          price: `-${formatPrice(item.totals.discount)}`,
        }));

      if (this.bundle && this.bundle.totals?.discount) {
        items.push({
          name: this.bundle.name,
          price: `-${formatPrice(this.bundle.totals.discount)}`,
        })
      }
      return items;
    },

    isModifyCheckout(): boolean {
      return !!this.originalItemTotalPrice;
    },

    isOnTravelExtrasPage(): boolean {
      return this.$route.name === ROUTE_NAMES.travel_extras
    },

    showCancellationFee(): boolean | string {
      return this.isCancelling && this.cancellationFee;
    },

    originalOrderProps(): Record<string, any> {
      const item = this.originalOrder;
      if (!item.inventory_option) {
        return {};
      }
      const sub_options = item.inventory_option.sub_options || [];
      const labels: Record<string, string> = {
        senior: 'Senior',
        adult: 'Adult',
        child: 'Child',
        infant: 'Infant',
      };
      const subItems: SubItems[] = [];
      const inventoryMap: Record<string, any> = {};
      sub_options.forEach((listItem) => {
        if (listItem?.details?.name) {
          inventoryMap[listItem.details.name] = listItem;
        }
      });
      ['senior', 'adult', 'child', 'infant'].forEach((key) => {
        const item = inventoryMap[key];
        if (!item) {
          return;
        }
        // Added label[key] until cart data response has added name_formatted
        const name = item.details?.name_formatted
          ? item.details.name
          : labels[key];
        const totals =
          'original_amounts' in item
            ? item.original_amounts.totals
            : item.totals;
        const quantity = item.quantity || totals.quantity;
        if (quantity) {
          const label = `${quantity} x ${name}`;
          subItems.push({
            title: label,
            value: formatPrice(totals.subtotal),
          });
          if (item.discount) {
            subItems.push({
              isDiscount: true,
              title: item.discount.description,
              value: formatPrice(item.discount.amount),
            });
          }
        }
      });

      const displayName =
        item?.inventory_item && 'display_name' in item.inventory_item
          ? item.inventory_item.display_name
          : '';

      const orderName = displayName || item?.inventory_item?.name || '';

      return {
        taxes: item.taxes,
        fees: item.fees,
        orderName,
        subItems,
      };
    },

    cancellationProtection(): Record<string, any> {
      const cancellationProtectionProduct = {
        is_protected: false,
        product_code: 'cancellation_protection',
        inventory_item: {
          name: this.$t('shared.summaryLounges.cancellationProtection'),
        },
        totals: {
          total: 0
        },
      };

      this.items.forEach((item) => {
        if ((item.is_protected || item.cancellation_policies?.is_protected) && !item.bundle_id) {
          const cancellationProtectionPrice = item.cancellation_protection?.totals?.total ?? 0;

          cancellationProtectionProduct.is_protected = true;
          cancellationProtectionProduct.totals.total = cancellationProtectionProduct.totals.total + cancellationProtectionPrice;
        }
      });

      if (this.bundle && this.bundle.items && !this.bundle.is_cancellation_protection_auto_add) {
        this.bundle.items.forEach((item) => {
          if (item.is_protected || item.cancellation_policies?.is_protected) {
            const cancellationProtectionPrice = item.cancellation_protection?.totals?.total ?? 0;

            cancellationProtectionProduct.is_protected = true;
            cancellationProtectionProduct.totals.total = cancellationProtectionProduct.totals.total + cancellationProtectionPrice;
          }
        });
      }

      return cancellationProtectionProduct;
    },

    isAmending(): boolean {
      const item = this.items[0];
      return item && 'meta' in item && !!item.meta?.original_order_item_id;
    },

    isCancelling(): boolean {
      return !!this.cancellableItemsIds && !!this.cancellableItemsIds.length;
    },

    isBundleSelectedForCancellation(product: any): boolean {
      return this.bundle
        && this.bundle.items.every((item) => this.cancellableItemsIds.some((id) => Number(id) === Number(item.id)));
    },

    originalItemTotalPrice(): string {
      const item = this.items[0];
      if (item && 'meta' in item) {
        return `${item?.meta?.original_order_item_total_price || ''}`;
      }
      return '';
    },

    /**
     * Text to be display when in modify/amendment flow for either refunds or payment due
     */
    modifyFlow(): VueI18n.TranslateResult {
      const useDepositOnly = useDepositStore();

      if (useDepositOnly.shouldShowDepositInformation) {
        if (this.isRefund) {
          // We own the customer
          return this.$t('shared.amountRefund');
        } else {
          // Customer owns us
          return this.$t('shared.payNow');
        }
      }

      if (this.isRefund) {
        return this.$t('shared.refundAmount');
      }

      if (this.amountToPay === 0) {
        return this.$t('shared.total');
      }

      return this.$t('shared.total');
    },

    /**
     * Text to be display when in the normal checkout flow
     */
    normalFlow(): VueI18n.TranslateResult {
      const useDepositOnly = useDepositStore();
      if (useDepositOnly.shouldShowDepositInformation) {
        return this.$t('shared.payNow');
      }

      return this.$t('shared.total');
    },

    issuingRefund(): VueI18n.TranslateResult {
      if (this.amountToPay === 0) {
        return this.$t('shared.total');
      }

      const useDepositOnly = useDepositStore();
      if (useDepositOnly.shouldShowDepositInformation) {
        return this.$t('shared.amountRefund');
      }

      return this.$t('shared.refundAmount');
    },

    totalLabel(): VueI18n.TranslateResult {
      if (this.isModifyCheckout) {
        return this.modifyFlow;
      }

      if (this.isModifyFlowReceipt) {
        if (this.amountToPay !== null && this.amountToPay < 0) {
          return this.$t('shared.refundAmount');
        }
        if (this.amountToPay !== null && this.amountToPay > 0) {
          return this.$t('shared.amountPaid');
        }
        return this.$t('shared.priceDifference');
      }

      if (this.amountToPay !== null && this.amountToPay > 0) {
        return this.$t('shared.total');
      }

      if (this.isRefund) {
        if (this.amountToPay === null) {
          return this.refundProcessing ? this.$t('shared.refundAmount') : this.$t('shared.amountRefund');
        }
        return this.issuingRefund;
      }

      if (this.hasPaid) {
        return this.$t('shared.totalPaid');
      }

      return this.normalFlow;
    },
    orderTotalFormatted(): string | Intl.NumberFormat {
      return typeof this.orderTotals?.total === 'number'
        ? formatPrice(this.orderTotals.total)
        : '';
    },
    extendedItemPrice(): string | Intl.NumberFormat {
      return typeof this.items[0]?.totals?.total === 'number'
        ? formatPrice(this.items[0]?.totals?.total)
        : '';
    },
    setAmendTitle() {
      if (this.isExtending && this.parkingExtensionEnabled) {
        const date1 = new Date(this.items?.[0]?.search_criteria?.parking?.date1);
        const date2 = new Date(this.items?.[0]?.search_criteria?.parking?.date2);
        const msPerDay = 1000 * 60 * 60 * 24;

        const days = Math.round((date2.getTime() - date1.getTime()) / msPerDay);
        return this.$t('shared.summaryParkings.extendedDays', { days: days });
      } else if (this.isAmending) {
        return this.$t('shared.summaryLounges.newBooking');
      }
      return '';
    },
    priceDifferenceFormatted(): ReturnType<typeof formatPrice> {
      return formatPrice(this.priceDifference);
    },
    totalAfterRefundsFormatted(): ReturnType<typeof formatPrice> {
      let totalPaid = (this.orderTotals.total - this.priceDifference) - this.orderTotals.cancellation_fee_total;
      return formatPrice(totalPaid)
    },

    isCancelled(): boolean {
      return !!(this.items.find((item) => {
        return item.status === 'cancelled';
      }));
    },

    totalValue(): string | Intl.NumberFormat {
      const useDepositOnly = useDepositStore();

      if (
        useDepositOnly.cartTotalDepositAmount
        && !(this.isModifyCheckout || this.isCancelling)
      ) {

        // If were on the ecom receipt page after doing an amendment, show the price without the amendment fee
        if (
          (this.$route.name === 'receipt' && this.$route.query['modify-receipt'])
          && this.orderTotals.paid_without_amendment_fee_formatted
        ) {
          return this.orderTotals.paid_without_amendment_fee_formatted.with_currency_sign;
        }

        if (this.valueAfterRefundWithoutCancellation) {
          return this.valueAfterRefundWithoutCancellation;
        }

        return this.isRefund || this.isModifyCheckout
          ? this.priceDifference
          :  useDepositOnly.cartTotalDepositAmount.payNow;
      }

      if (!this.isModifyFlowReceipt) {
        if (this.valueAfterRefundWithoutCancellation) {
          return this.valueAfterRefundWithoutCancellation;
        }

        if (this.isPartialRefund) {
          return this.totalAfterRefundsFormatted;
        }

        if (this.isExtended) {
          return this.extendedItemPrice;
        }

        return this.isRefund || this.isModifyCheckout
          ? this.priceDifference
          : this.orderTotalFormatted;
      }

      return this.priceDifference;
    },
    infoMessage(): InfoMessage | string {
      const titleKey = this.originalOrder.product_code === 'parking'
        ? 'shared.summaryParkings'
        : 'shared.summaryLounges';
      if (this.amountToPay !== null && this.amountToPay > 0) {
        return {
          text: titleKey + '.originalPriceIsLower',
          slot0: titleKey + '.originalPriceIsLowerSlot0',
        };
      }
      if (this.amountToPay !== null && this.amountToPay < 0) {
        return {
          text: titleKey + '.originalPriceIsHigher',
          slot0: titleKey + '.originalPriceIsHigherSlot0',
        };
      }
      if (this.amountToPay === 0) {
        return {
          text: titleKey + '.sameAsOriginalPrice',
          slot0: titleKey + '.sameAsOriginalPriceSlot0',
        };
      }
      if (this.refundDoesNotExist) {
        return {
          text: titleKey + '.refundDoesNotExist',
        };
      }
      if (this.refundProcessing) {
        return {
          text: titleKey + '.refundProcessing',
        };
      }
      return '';
    },

    /**
     * Returns the summary text based on state of product.
     * If a deposit has been paid, it returns 'Deposit paid',
     * otherwise it returns the translated value of 'shared.summaryParkings.totalPaid'.
     *
     * @return {VueI18n.TranslateResult} The summary text.
     */
    summaryText(): VueI18n.TranslateResult {
      const useDepositOnly = useDepositStore();
      if (useDepositOnly.shouldShowDepositInformation) {
        return this.$t('shared.depositPaid');
      }

      return this.$t('shared.summaryLounges.totalPaid');
    },

    /**
     * Checks if the current page is a receipt page.
     *
     * @returns Returns true if the current page is a receipt page, false otherwise.
     */
    isReceiptPage(): boolean {
      // @ts-ignore
      return this.$route && this.$route.name.includes('receipt');
    },

    /**
     * Determines if the current route is a manage booking page.
     *
     * @returns True if the current route is a manage booking page, otherwise false.
     */
    isManageBookingPages(): boolean {
      // @ts-ignore
      return this.$route && this.$route.name.includes('manage-booking');
    },

    displayDiscountSection(): boolean {
      return this.orderTotals.discount !== 0
        && this.discountItems.length !== 0
        && this['$launchDarkly'].variation(FEATURE_FLAGS.PT_1566_DISPLAY_NEW_PAYMENT_SUMMARIES);
    },

    combinedTaxes(): TaxFee[] {
      // Create a map to store unique items by their name
      const itemMap = new Map();

      this.items.forEach((item) => {
        item.taxes.forEach((tax) => {
          // If the item name is already in the map, sum the amounts
          if (itemMap.has(tax.name)) {
            itemMap.get(tax.name).amount += tax.amount;
          } else {
            // Otherwise, add the item to the map
            itemMap.set(tax.name, { ...tax });
          }
        })
      });

      // Uncomment this when the backend will calculate taxes correctly
      // if (this.$launchDarkly.variation(FEATURE_FLAGS.ECOM_3307_SHOW_BOOKING_FEE_AS_LINE_ITEM)) {
      //   this.bookingFee.taxes.forEach((tax) => {
      //     // If the item name is already in the map, sum the amounts
      //     if (itemMap.has(tax.name)) {
      //       itemMap.get(tax.name).amount += tax.amount;
      //     } else {
      //       // Otherwise, add the item to the map
      //       itemMap.set(tax.name, { ...tax });
      //     }
      //   })
      // }

      // Convert the map values back to an array
      return Array.from(itemMap.values());
    },

    taxesTotal(): number {
      return this.items.reduce((previousItem, currentItem) => previousItem + currentItem.totals.taxes ,0);
    },

    displayTaxesSection(): boolean {
      return this.combinedTaxes.length !== 0
        && (
          this.forceTaxesBreakdown
          || ((!getAppVariable('is_tax_inclusive') || this.isUsPartner) && this['$launchDarkly'].variation(FEATURE_FLAGS.PT_1566_DISPLAY_NEW_PAYMENT_SUMMARIES))
        );
    },

    bookingFeeAmountFormatted(): string | Intl.NumberFormat {
      return formatPrice(this.bookingFee.amount);
    },
    FEATURE_FLAGS(): typeof FEATURE_FLAGS {
      return FEATURE_FLAGS;
    },
    isUsPartner() {
      return getAppVariable('poi.country.code') === 'US';
    },
    merchantId() {
      return getAppVariable('review_providers[0].merchant_id');
    },
    feefoReviewsWithinFlow(): boolean {
      return getFeatureFlag(FEATURE_FLAGS.CRO_844_FEEFO_REVIEW_WITHIN_FLOW, false) === 'VARIANT_B';
    },
    singleDiscountMode(): boolean {
      // `discountCode` prop will be set only on Payment and Travel-extras pages and new UI can be shown only on these pages
      // regardless of flag value
      return getFeatureFlag(FEATURE_FLAGS.CRO_1027_DISCOUNT_CODE_PAYMENT_SECTION) === true && !!this.discountCode;
    },
  },
  mounted() {
    const { path } = this.$route || {};
    this.isExtrasRoute = path?.includes(NAMED_ROUTES.select_extras.product) ?? false;

    if (this.isUsPartner) this.showTaxesBreakdown = true;

    if (this.isSticky) {
      this.initSummaryHeightObserver();
      window.addEventListener('resize', this.updateSummaryHeight);
    }
  },
  beforeDestroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.updateSummaryHeight);
    document.documentElement.style.setProperty('--summary_bottom_height', '0px');
  },
  methods: {
    toggleDropdown() {
      this.dropdownIsActive = !this.dropdownIsActive;
    },
    isExtrasProduct(product){
      return product.product_code === PRODUCT_TYPES.EXTRAS;
    },
    formatPrice,
    onClickCancellationProtectionHandler() {
      this.isCancellationProtectionExpanded =
        !this.isCancellationProtectionExpanded;
    },

    isProductSelectedForCancellation(product: any): boolean {
      return this.cancellableItemsIds
        && this.cancellableItemsIds.some((id) => id.toString() === product.id.toString());
    },

    getPartialRefundAmount(cartItem: any): string {
      const refundableAmount = cartItem.totals.total - cartItem.totals.refunded;
      return formatPrice(refundableAmount);
    },

    toggleBreakdown(field): void {
      this[field] = !this[field];
    },

    //for scrolling summary to the top when using keyboard navigation
    scrollToTop() {
      const container = this.$refs.scrollingContainer as HTMLElement;
      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      }
    },

    //for payment summary - so it doesn't overlap with the footer on mobile
    updateSummaryHeight() {
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        const height = this.$el?.offsetHeight || 0;
        document.documentElement.style.setProperty(
          '--summary_bottom_height', `${height}px`
        );
      } else {
        document.documentElement.style.setProperty('--summary_bottom_height', '0px');
      }
    },

    initSummaryHeightObserver() {
      this.$nextTick(() => {
        this._resizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(() => {
            this.updateSummaryHeight();
          });
        });
        if (this.$el) {
          this._resizeObserver.observe(this.$el);
        }
      });
    }
  },
});
